import React, { useState } from 'react';
import { ArrowRight, Plus, Server, Users, ShieldCheck, AlertTriangle, Cloud, Database, Activity, Shield, Settings, FileText } from 'lucide-react';
export interface Environment {
  id: string;
  clientName: string;
  type: 'AWS' | 'Azure' | 'GCP' | 'Hybrid' | 'On-Prem';
  status: 'healthy' | 'warning' | 'critical';
  identities: number;
  complianceScore: number;
  overPrivileged: number;
  lastSync: string;
  govCloud?: boolean;
  govCloudRegion?: string;
  cmmcLevel?: number;
  complianceFrameworks?: string[];
}
interface ManagedEnvironmentsProps {
  environments: Environment[];
  onAddEnvironment: () => void;
  onViewAll: () => void;
  onManage: (env: Environment) => void;
  onViewVulns: (env: Environment) => void;
  onGenerateReport?: (env: Environment) => void;
}
export function ManagedEnvironments({
  environments,
  onAddEnvironment,
  onViewAll,
  onManage,
  onViewVulns,
  onGenerateReport
}: ManagedEnvironmentsProps) {
  const [filter, setFilter] = useState<'All' | 'Cloud' | 'Hybrid' | 'On-Prem' | 'Critical'>('All');
  const filteredEnvs = environments.filter(env => {
    switch (filter) {
      case 'Cloud':
        return env.type === 'AWS' || env.type === 'Azure' || env.type === 'GCP';
      case 'Hybrid':
        return env.type === 'Hybrid';
      case 'On-Prem':
        return env.type === 'On-Prem';
      case 'Critical':
        return env.status === 'critical' || env.status === 'warning';
      default:
        return true;
    }
  });
  const filterTabs = [{
    id: 'All' as const,
    label: 'All',
    count: environments.length
  }, {
    id: 'Cloud' as const,
    label: 'Cloud',
    count: environments.filter(e => e.type === 'AWS' || e.type === 'Azure' || e.type === 'GCP').length
  }, {
    id: 'Hybrid' as const,
    label: 'Hybrid',
    count: environments.filter(e => e.type === 'Hybrid').length
  }, {
    id: 'On-Prem' as const,
    label: 'On-Prem',
    count: environments.filter(e => e.type === 'On-Prem').length
  }, {
    id: 'Critical' as const,
    label: 'At Risk',
    count: environments.filter(e => e.status === 'critical' || e.status === 'warning').length
  }];

  // Filter logic could be expanded, for now just showing UI state
  const getStatusColor = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return 'bg-astro-green';
      case 'warning':
        return 'bg-astro-text-secondary';
      case 'critical':
        return 'bg-astro-danger';
      default:
        return 'bg-astro-text-secondary';
    }
  };
  const getStatusBorder = (status: Environment['status']) => {
    switch (status) {
      case 'healthy':
        return 'group-hover:border-astro-green/50';
      case 'warning':
        return 'group-hover:border-white/20';
      case 'critical':
        return 'group-hover:border-astro-danger/50';
      default:
        return 'group-hover:border-white/20';
    }
  };
  const getTypeIcon = (type: Environment['type']) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={14} data-id="element-560" />;
      case 'On-Prem':
        return <Server size={14} data-id="element-561" />;
      case 'Hybrid':
        return <Database size={14} data-id="element-562" />;
      default:
        return <Server size={14} data-id="element-563" />;
    }
  };
  const getCmmcColor = (level: number) => {
    if (level >= 3) return 'text-astro-green bg-astro-green/10 border-astro-green/30';
    if (level >= 2) return 'text-astro-text-secondary bg-white/5 border-white/10';
    return 'text-astro-danger bg-astro-danger/10 border-astro-danger/20';
  };
  return <div className="mt-8" data-id="element-564">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4" data-id="element-565">
        <div data-id="element-566">
          <h2 className="text-xl font-bold text-white font-mono" data-id="element-567">
            Managed Environments
          </h2>
          <p className="text-sm text-astro-text-secondary mt-1" data-id="element-568">
            Centralized IAM across client environments
          </p>
        </div>
        <div className="flex items-center gap-4" data-id="element-569">
          <button onClick={onViewAll} className="text-sm font-medium text-astro-green hover:text-astro-green-dark flex items-center gap-1 transition-colors" data-id="element-570">

            View All <ArrowRight size={16} data-id="element-571" />
          </button>
          <button onClick={onAddEnvironment} className="flex items-center gap-2 px-4 py-2 bg-astro-green-dark/20 text-astro-green border border-astro-green/30 rounded text-sm font-medium hover:bg-astro-green-dark/30 transition-colors" data-id="element-572">

            <Plus size={16} data-id="element-573" />
            Add Environment
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 sm:pb-0" data-id="element-574">
        {filterTabs.map(tab => <button key={tab.id} onClick={() => setFilter(tab.id)} className={`
              px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
              ${filter === tab.id ? tab.id === 'Critical' ? 'bg-astro-danger/15 text-astro-danger border border-astro-danger/30' : 'bg-astro-green/15 text-astro-green border border-astro-green/30' : 'text-astro-text-secondary hover:text-white border border-transparent hover:border-astro-border'}
            `} data-id="element-575">

            {tab.label}
            <span className={`ml-2 text-[10px] ${filter === tab.id ? 'opacity-80' : 'opacity-60'}`} data-id="element-576">

              {tab.count}
            </span>
          </button>)}
      </div>

      {/* Environment Cards */}
      <div className="space-y-4" data-id="element-577">
        {filteredEnvs.length === 0 && <div className="py-12 text-center bg-astro-card border border-astro-border rounded-lg" data-id="element-578">
            <p className="text-sm text-astro-text-secondary" data-id="element-579">
              No environments match this filter.
            </p>
          </div>}
        {filteredEnvs.map(env => <div key={env.id} className={`group relative bg-astro-card border border-astro-border rounded-lg p-5 transition-all duration-300 ${getStatusBorder(env.status)}`} data-id="element-580">

            {/* Left Accent Bar */}
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusColor(env.status)} opacity-60 group-hover:opacity-100 transition-opacity`} data-id="element-581">
          </div>

            <div className="flex flex-col lg:flex-row gap-6 pl-2" data-id="element-582">
              {/* Column 1: Identity & Status */}
              <div className="flex-1 min-w-[200px]" data-id="element-583">
                <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-584">
                  <h3 className="font-bold text-white text-lg" data-id="element-585">
                    {env.clientName}
                  </h3>
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-white/5 text-astro-text-secondary border-white/10" data-id="element-586">
                    {getTypeIcon(env.type)}
                    {env.type}
                  </span>
                  {env.govCloud && <span className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-blue-500/10 text-blue-400 border-blue-500/25" data-id="element-587">
                      <Shield size={10} data-id="element-588" />
                      GOV
                    </span>}
                  {env.cmmcLevel != null && <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${getCmmcColor(env.cmmcLevel)}`} data-id="element-589">

                      CMMC L{env.cmmcLevel}
                    </span>}
                </div>

                <div className="flex items-center gap-2 text-xs text-astro-text-secondary font-mono flex-wrap" data-id="element-590">
                  <span className="flex items-center gap-1.5" data-id="element-591">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor(env.status)} animate-pulse`} data-id="element-592">
                  </div>
                    {env.status.toUpperCase()}
                  </span>
                  <span className="text-astro-border" data-id="element-593">•</span>
                  <span data-id="element-594">ID: {env.id}</span>
                  <span className="text-astro-border" data-id="element-595">•</span>
                  <span data-id="element-596">Synced: {env.lastSync}</span>
                  {env.govCloudRegion && <>
                      <span className="text-astro-border" data-id="element-597">•</span>
                      <span className="text-blue-400/70" data-id="element-598">
                        {env.govCloudRegion}
                      </span>
                    </>}
                </div>

                {/* Compliance Frameworks */}
                {env.complianceFrameworks && env.complianceFrameworks.length > 0 && <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-id="element-599">
                      {env.complianceFrameworks.map(fw => <span key={fw} className="px-1.5 py-0.5 rounded text-[9px] font-mono font-medium text-astro-text-secondary/70 bg-white/[0.03] border border-white/[0.06]" data-id="element-600">

                          {fw}
                        </span>)}
                    </div>}
              </div>

              {/* Column 2: Stats Grid */}
              <div className="flex-[2] grid grid-cols-2 sm:grid-cols-3 gap-4 lg:gap-8 items-center" data-id="element-601">
                {/* Identities */}
                <div className="flex flex-col" data-id="element-602">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-603">
                    <Users size={12} data-id="element-604" /> Identities
                  </span>
                  <span className="text-lg font-mono font-bold text-white" data-id="element-605">
                    {env.identities.toLocaleString()}
                  </span>
                </div>

                {/* Compliance */}
                <div className="flex flex-col w-full" data-id="element-606">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-607">
                    <ShieldCheck size={12} data-id="element-608" /> Compliance
                  </span>
                  <div className="flex items-center gap-2" data-id="element-609">
                    <div className="flex-1 h-1.5 bg-astro-bg rounded-full overflow-hidden border border-astro-border/50" data-id="element-610">
                      <div className={`h-full rounded-full ${env.complianceScore > 85 ? 'bg-astro-green' : env.complianceScore > 70 ? 'bg-astro-text-secondary' : 'bg-astro-danger'}`} style={{
                    width: `${env.complianceScore}%`
                  }} data-id="element-611">
                    </div>
                    </div>
                    <span className={`text-sm font-mono font-bold ${env.complianceScore > 85 ? 'text-astro-green' : env.complianceScore > 70 ? 'text-astro-text-secondary' : 'text-astro-danger'}`} data-id="element-612">

                      {env.complianceScore}%
                    </span>
                  </div>
                </div>

                {/* Risk / Over-privileged */}
                <div className="flex flex-col" data-id="element-613">
                  <span className="text-[10px] text-astro-text-secondary uppercase tracking-wider mb-1 flex items-center gap-1" data-id="element-614">
                    <AlertTriangle size={12} data-id="element-615" /> Risk
                  </span>
                  <div className="flex items-center gap-2" data-id="element-616">
                    <span className="text-lg font-mono font-bold text-white" data-id="element-617">
                      {env.overPrivileged}
                    </span>
                    <span className="text-xs text-astro-text-secondary" data-id="element-618">
                      over-privileged
                    </span>
                  </div>
                </div>
              </div>

              {/* Column 3: Actions */}
              <div className="flex items-center justify-end gap-3 lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t lg:border-t-0 border-astro-border/50" data-id="element-619">
                <button onClick={() => onViewVulns(env)} className="flex items-center gap-2 px-4 py-2 bg-astro-bg border border-astro-border rounded text-sm font-medium text-astro-text-secondary hover:text-white hover:border-astro-green/50 hover:bg-astro-green/5 transition-all whitespace-nowrap" data-id="element-620">

                  <Shield size={16} data-id="element-621" />
                  Issues
                </button>
                {onGenerateReport && <button onClick={() => onGenerateReport(env)} className="flex items-center gap-2 px-4 py-2 bg-astro-bg border border-astro-border rounded text-sm font-medium text-astro-text-secondary hover:text-white hover:border-astro-green/50 hover:bg-astro-green/5 transition-all whitespace-nowrap" data-id="element-622">

                    <FileText size={16} data-id="element-623" />
                    Report
                  </button>}
                <button onClick={() => onManage(env)} className="flex items-center gap-2 px-4 py-2 bg-astro-bg border border-astro-border rounded text-sm font-medium text-astro-text-secondary hover:text-white hover:border-astro-green/50 hover:bg-astro-green/5 transition-all whitespace-nowrap" data-id="element-624">

                  <Settings size={16} data-id="element-625" />
                  Manage
                </button>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}